import type { Appearance, CSSProps } from '@whop/elements-react';

import type { Palette } from './palette';
import { PALETTE } from './palette';

const DEFAULT_FONT_FAMILY = 'FragmentMono, ui-monospace, monospace';
// `text-md` in fragment-ui.
const DEFAULT_FONT_SIZE = '20px';
const DEFAULT_BORDER_RADIUS = '0px';

// fragment-ui's `text-md` is 20px over 27px.
const LINE_HEIGHT_RATIO = 27 / 20;

/** Class lists from the Styling section of each Whop element's docs page. */
const CLASSES_BY_ROLE = {
  root: ['whop-Address', 'whop-Payment'],
  input: [
    'whop-AddressFieldInput',
    'whop-AddressFieldInputInvalid',
    'whop-AddressFieldSelect',
    'whop-CardFieldInput',
    'whop-CardFieldInputInvalid',
    'whop-PaymentFieldInput',
    'whop-PaymentFieldInputInvalid',
    'whop-PaymentPayerDocumentType',
  ],
  // Whop exposes no hover class, so focus carries the interactive state.
  inputFocused: ['whop-CardFieldInputFocused'],
  label: [
    'whop-AddressFieldLabel',
    'whop-CardLabel',
    'whop-PaymentFieldLabel',
    'whop-PaymentPayerDocumentLabel',
    'whop-PaymentInstallmentsLabel',
  ],
  error: [
    'whop-AddressErrorSummary',
    'whop-AddressFieldError',
    'whop-CardFieldError',
    'whop-PaymentFieldError',
    'whop-PaymentPayerDocumentError',
    'whop-PaymentError',
  ],
  secondaryText: [
    'whop-AddressLine2Toggle',
    'whop-AddressManualEntry',
    'whop-AddressSuggestionManual',
    'whop-AddressSuggestionsEmpty',
    'whop-PaymentDetailText',
    'whop-PaymentDetailSubtext',
    'whop-PaymentSaveNotice',
    'whop-PaymentSettlementNotice',
    'whop-PaymentMandateNotice',
    'whop-PaymentSavedMethodHint',
    'whop-PaymentBalanceHint',
    'whop-PaymentMoreRow',
    'whop-PaymentInstallmentDetail',
    'whop-PaymentInstallmentsNotice',
    'whop-PaymentCurrencyFallback',
  ],
  primaryText: [
    'whop-PaymentMethodLabel',
    'whop-PaymentMandateLink',
    'whop-PaymentInstallmentLabel',
    'whop-AddressSuggestion',
  ],
  row: [
    'whop-PaymentMethodRow',
    'whop-PaymentMethodTile',
    'whop-PaymentSavedMethodRow',
    'whop-PaymentBalanceRow',
    'whop-PaymentInstallmentRow',
    'whop-PaymentMethodMoreTile',
  ],
  rowSelected: [
    'whop-PaymentMethodRowSelected',
    'whop-PaymentMethodTileSelected',
    'whop-PaymentSavedMethodRowSelected',
    'whop-PaymentBalanceRowSelected',
    'whop-PaymentInstallmentRowSelected',
  ],
  panel: [
    'whop-PaymentMethods',
    'whop-PaymentMethodsSeparated',
    'whop-PaymentMethod',
    'whop-PaymentMethodSeparated',
    'whop-PaymentMethodPanel',
    'whop-PaymentMethodDetail',
    'whop-PaymentDetailRegion',
    'whop-PaymentCardFields',
    'whop-PaymentBillingBlock',
    'whop-CardFieldGroup',
    'whop-PaymentSavedMethods',
    'whop-PaymentInstallments',
    'whop-PaymentBalanceList',
  ],
  overlay: ['whop-AddressSuggestions'],
  overlayActive: ['whop-AddressSuggestionActive'],
  radio: ['whop-PaymentMethodRadio', 'whop-PaymentInstallmentRadio'],
  radioSelected: ['whop-PaymentMethodRadioSelected'],
} satisfies Record<string, string[]>;

type Role = keyof typeof CLASSES_BY_ROLE;

type ResolvedTheme = {
  colors: Palette;
  fontFamily: string;
  fontSize: string;
  borderRadius: string;
};

// A size Whop can scale against is a plain pixel length. Anything else keeps
// Whop's own line height rather than guessing the root font size, which this
// code cannot see from outside the frame.
const toLineHeight = (fontSize: string): CSSProps => {
  const pixels = /^(\d+(?:\.\d+)?)px$/.exec(fontSize)?.[1];
  return pixels === undefined
    ? {}
    : { lineHeight: `${Math.round(Number(pixels) * LINE_HEIGHT_RATIO)}px` };
};

const makeDeclarationsByRole = ({
  colors,
  fontFamily,
  fontSize,
  borderRadius,
}: ResolvedTheme): Record<Role, CSSProps> => {
  const type: CSSProps = {
    fontFamily,
    fontSize,
    fontWeight: '400',
    ...toLineHeight(fontSize),
  };
  const shape: CSSProps = { borderRadius, boxShadow: 'none' };
  const text: CSSProps = { color: colors.text };
  const muted: CSSProps = { color: colors.mutedText };
  const input: CSSProps = {
    backgroundColor: colors.inputBackground,
    ...text,
    ...type,
    ...shape,
    border: 'none',
    padding: '0px',
  };
  const row: CSSProps = {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    ...text,
    ...type,
    ...shape,
    borderWidth: '0.5px',
    borderStyle: 'solid',
  };
  return {
    root: { ...text, ...type },
    input,
    inputFocused: { ...input, backgroundColor: colors.inputFocusBackground },
    label: { ...muted, ...type },
    error: { color: colors.error, ...type },
    secondaryText: { ...muted, ...type },
    primaryText: { ...text, ...type },
    row,
    rowSelected: { ...row, backgroundColor: colors.inputBackground },
    panel: { ...shape, border: 'none', backgroundColor: 'transparent' },
    overlay: {
      backgroundColor: colors.inputFocusBackground,
      borderColor: colors.border,
      ...shape,
      borderWidth: '0.5px',
      borderStyle: 'solid',
    },
    overlayActive: { backgroundColor: colors.inputBackground },
    radio: { borderColor: colors.text, ...shape },
    radioSelected: {
      borderColor: colors.text,
      backgroundColor: colors.text,
      ...shape,
    },
  };
};

// Mirrors the SDK's default appearance; theme.test.ts checks both palettes.
export const makeAppearance = (appearance: 'light' | 'dark'): Appearance => {
  const resolved: ResolvedTheme = {
    colors: PALETTE[appearance],
    fontFamily: DEFAULT_FONT_FAMILY,
    fontSize: DEFAULT_FONT_SIZE,
    borderRadius: DEFAULT_BORDER_RADIUS,
  };
  const declarations = makeDeclarationsByRole(resolved);
  const roles = Object.keys(CLASSES_BY_ROLE) as Role[];
  return {
    theme: { appearance, accentColor: 'gray', grayColor: 'gray' },
    variables: { '--radius': resolved.borderRadius },
    classes: Object.fromEntries(
      roles.flatMap((role) =>
        CLASSES_BY_ROLE[role].map((className) => [
          className,
          declarations[role],
        ])
      )
    ),
  };
};
